/**
 * Reading a reference field back off a response when the backend may send one
 * or several.
 *
 * The goal is the field this exists for. It used to be a single `{ id, name }`
 * under `goal`; a client and an enquiry can now be signed up against more than
 * one, so it comes back under `goals` — and the two write endpoints take a list
 * of ids. Three separate responses carry it (a client, an enquiry, a dashboard
 * row), so the reading of it lives here rather than being written out three
 * times in three services.
 *
 * Deliberately forgiving about the shape. Any of these is read as the same
 * list, and each is something a real response has been seen to carry:
 *
 *   "goals": [{ id, name }, …]   the list, which is what a multi-goal record is
 *   "goals": { id, name }        one goal, sent unwrapped
 *   "goal":  { id, name }        the old field name, on a record not migrated
 *   "goals": [{ public_id, name }, …]
 *                                a client's goals, which are the practitioner's
 *                                own services now and carry a UUID rather than
 *                                a catalog id
 *   null / missing               no goal on the record
 *
 * That tolerance is the point: a screen that reads a dash where the client has
 * a goal is a bug nobody sees until somebody notices the column is empty, and
 * the cost of guarding against it is this function.
 */

/**
 * A row of a backend reference list, as this reads it out: the name, and
 * whichever id came with it — a catalog's number, or the `public_id` string a
 * practitioner service is identified by.
 */
export interface LookupReference {
  id: number | string;
  name: string;
}

/**
 * A row as it arrives, before the two spellings of its id are read as one.
 * `id` on a catalog row, `public_id` on a service.
 */
interface RawReference {
  id?: number | string | null;
  public_id?: string | null;
  name: string;
}

/** Anything a reference field can arrive as — one, several, or nothing. */
export type LookupField =
  | RawReference
  | RawReference[]
  | null
  | undefined;

/** Whether a value is a usable named row rather than a null or a stray. */
const isReference = (value: unknown): value is RawReference =>
  !!value &&
  typeof value === "object" &&
  typeof (value as RawReference).name === "string";

/**
 * One row with its id read from whichever key carried it. A row that came with
 * neither is kept rather than dropped, with `""` for the id: the name is what
 * every screen draws, and losing a client's goal because the backend sent it
 * without an id is the worse failure. Nothing writes these ids back — a save
 * resolves the name against the list the form is holding.
 */
const toReference = (row: RawReference): LookupReference => ({
  id: row.id ?? row.public_id ?? "",
  name: row.name,
});

/**
 * A reference field as a list, whichever of the shapes above it arrived in.
 * Rows the backend sent without a name are dropped rather than rendered as
 * blanks — see the note above.
 */
export const toLookupReferences = (value: LookupField): LookupReference[] => {
  if (Array.isArray(value)) return value.filter(isReference).map(toReference);
  return isReference(value) ? [toReference(value)] : [];
};

/**
 * The first of them, or `null`. For the places that still hold one — a record
 * mapped before this field grew a list, or a caller that genuinely wants the
 * primary answer.
 */
export const toLookupReference = (
  value: LookupField
): LookupReference | null => toLookupReferences(value)[0] ?? null;
